import useI18n from '@/shared/hooks/useI18n'
import { BodyTd, FooterTd, HeaderTh, TableBody, TableBodyRow } from './typography'
import { MethodAggregatedEmissions } from '@/hooks/api/finalizedReports'
import { compact } from 'es-toolkit'

export default function FinalizedReportCombinedElectricityEmissionsTable({
  methodAggregatedEmissions,
  strings,
  selectedPdfLanguage,
  showOnlyCombinedEmissions
}: {
  methodAggregatedEmissions: MethodAggregatedEmissions
  strings?: {
    electricityConsumed: string
    emissionFactor: string
    specificIndirectEmissions: string
    onSite: string
    precursor: string
    total: string
  }
  selectedPdfLanguage?: string
  showOnlyCombinedEmissions?: boolean
}) {
  const { numberFormatter3DecimalsGB, t } = useI18n()
  const {
    onSiteSpecificElectricityConsumption,
    onSiteElectricityCombinedEmissionFactor,
    onSiteSpecificElectricityEmissions,
    precursorElectricityConsumptionPerTofProduct,
    precursorElectricityCombinedEmissionFactor,
    precursorSpecificIndirectEmissions,
    specificEmbeddedIndirect,
    combinedElectricityEmissionFactor,
    combinedSpecificElectricityConsumption
  } = methodAggregatedEmissions

  const tLng = { lng: selectedPdfLanguage }
  const localStrings = {
    onSite: strings?.onSite ?? t('finalizedReportDocument:emissions.indirect.table.onSite', tLng),
    precursor:
      strings?.precursor ?? t('finalizedReportDocument:emissions.indirect.table.precursors', tLng),
    total:
      strings?.total ?? t('finalizedReportDocument:emissions.indirect.table.combinedRow', tLng),
    electricityConsumed:
      strings?.electricityConsumed ??
      t('finalizedReportDocument:emissions.indirect.table.electricityConsumed', tLng),
    emissionFactor:
      strings?.emissionFactor ??
      t('finalizedReportDocument:emissions.indirect.table.emissionFactor', tLng),
    specificIndirectEmissions:
      strings?.specificIndirectEmissions ??
      t('finalizedReportDocument:emissions.indirect.table.specificIndirectEmissions', tLng)
  }

  const onSite = {
    title: localStrings.onSite,
    electricityConsumption: onSiteSpecificElectricityConsumption,
    emissionFactor: onSiteElectricityCombinedEmissionFactor,
    specificIndirectEmissions: onSiteSpecificElectricityEmissions
  }

  const precursor = {
    title: localStrings.precursor,
    electricityConsumption: precursorElectricityConsumptionPerTofProduct,
    emissionFactor: precursorElectricityCombinedEmissionFactor,
    specificIndirectEmissions: precursorSpecificIndirectEmissions
  }

  const total = {
    title: localStrings.total,
    electricityConsumption: combinedSpecificElectricityConsumption,
    emissionFactor: combinedElectricityEmissionFactor,
    specificIndirectEmissions: specificEmbeddedIndirect
  }

  const hasPrecursorEmissions = precursor.specificIndirectEmissions > 0

  return (
    <table className="w-full">
      <thead>
        <tr>
          <HeaderTh className="w-28" />
          <HeaderTh>{localStrings.electricityConsumed}</HeaderTh>
          <HeaderTh>{localStrings.emissionFactor}</HeaderTh>
          <HeaderTh>{localStrings.specificIndirectEmissions}</HeaderTh>
        </tr>
      </thead>
      {!showOnlyCombinedEmissions && (
        <TableBody>
          {compact([hasPrecursorEmissions ? precursor : null, onSite]).map(
            ({ title, electricityConsumption, emissionFactor, specificIndirectEmissions }) => (
              <TableBodyRow key={title}>
                <BodyTd>{title}</BodyTd>
                <BodyTd isNumeric>
                  {numberFormatter3DecimalsGB(electricityConsumption)} MWh/t
                </BodyTd>
                <BodyTd isNumeric>{numberFormatter3DecimalsGB(emissionFactor)} tCO₂e/MWh</BodyTd>
                <BodyTd isNumeric>
                  {numberFormatter3DecimalsGB(specificIndirectEmissions)} tCO₂e/t
                </BodyTd>
              </TableBodyRow>
            )
          )}
        </TableBody>
      )}
      {showOnlyCombinedEmissions && (
        <TableBody>
          <TableBodyRow>
            <BodyTd>{total.title}</BodyTd>
            <BodyTd isNumeric>
              {numberFormatter3DecimalsGB(total.electricityConsumption)} MWh/t
            </BodyTd>
            <BodyTd isNumeric>{numberFormatter3DecimalsGB(total.emissionFactor)} tCO₂e/MWh</BodyTd>
            <BodyTd isNumeric>
              {numberFormatter3DecimalsGB(total.specificIndirectEmissions)} tCO₂e/t
            </BodyTd>
          </TableBodyRow>
        </TableBody>
      )}
      {hasPrecursorEmissions && !showOnlyCombinedEmissions && (
        <tfoot>
          <tr>
            <FooterTd>{total.title}</FooterTd>
            <FooterTd isNumeric>
              {numberFormatter3DecimalsGB(total.electricityConsumption)} MWh/t
            </FooterTd>
            <FooterTd isNumeric>
              {numberFormatter3DecimalsGB(total.emissionFactor)} tCO₂e/MWh
            </FooterTd>
            <FooterTd isNumeric>
              {numberFormatter3DecimalsGB(total.specificIndirectEmissions)} tCO₂e/t
            </FooterTd>
          </tr>
        </tfoot>
      )}
    </table>
  )
}
